import { useEffect, useState } from "react";
import { Link, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import PageHead from "@/components/PageHead";
import { PartnerAuthProvider, usePartner } from "@/context/PartnerAuthContext";
import { partnerApi, formatDateTime } from "@/lib/partnerApi";
import { PartnerShell, PageLoading, P_CARD, P_H1, BackLink } from "@/components/partner/PartnerUI";
import { PartnerLogin, PartnerForgot, PartnerSetPassword } from "@/pages/partner/PartnerAuthPages";
import PartnerHome from "@/pages/partner/PartnerHome";
import PartnerObra from "@/pages/partner/PartnerObra";
import PartnerUpdateForm from "@/pages/partner/PartnerUpdateForm";
import { PartnerNewThread, PartnerThread } from "@/pages/partner/PartnerThread";

function RequirePartner() {
    const { partner } = usePartner();
    const loc = useLocation();
    if (partner === null) return <PageLoading />;
    if (!partner) return <Navigate to="/parceiros/entrar" replace state={{ from: loc.pathname }} />;
    return <PartnerShell><Outlet /></PartnerShell>;
}

function PartnerNotifications() {
    const { refresh } = usePartner();
    const [items, setItems] = useState(null);

    useEffect(() => {
        partnerApi.get("/notifications").then(({ data }) => {
            setItems(data.items);
            if (data.unread > 0) partnerApi.post("/notifications/read").then(refresh).catch(() => {});
        }).catch(() => setItems([]));
    }, [refresh]);

    return (
        <>
            <BackLink to="/parceiros" label="As minhas obras" />
            <h1 className={P_H1}>Notificações</h1>
            {items === null ? <PageLoading /> : items.length === 0 ? (
                <div className={`${P_CARD} text-stone-500`} data-testid="partner-notifications-empty">Sem notificações.</div>
            ) : (
                <ul className={`${P_CARD} !p-2 divide-y divide-stone-100`} data-testid="partner-notifications-list">
                    {items.map((n) => (
                        <li key={n.id}>
                            <Link to={n.link || "/parceiros"} className="flex items-center gap-3 p-3.5" data-testid={`partner-notification-${n.id}`}>
                                {!n.read_at && <span className="w-2 h-2 rounded-full bg-[hsl(var(--copper))] flex-shrink-0" aria-label="Nova" />}
                                <span className="min-w-0 flex-1">
                                    <span className="block font-semibold text-stone-900 break-words">{n.title}</span>
                                    {n.body && <span className="block text-[15px] text-stone-600 break-words">{n.body}</span>}
                                    <span className="block text-sm text-stone-400 mt-0.5">{formatDateTime(n.created_at)}</span>
                                </span>
                                <ChevronRight className="w-5 h-5 text-stone-400 flex-shrink-0" />
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}

export default function PartnerApp() {
    return (
        <PartnerAuthProvider>
            <PageHead title="Área de Parceiros — O Homem da Obra" description="Área reservada a parceiros de O Homem da Obra." canonical={null} noindex />
            <Routes>
                <Route path="entrar" element={<PartnerLogin />} />
                <Route path="recuperar" element={<PartnerForgot />} />
                <Route path="definir-palavra-passe" element={<PartnerSetPassword />} />
                <Route element={<RequirePartner />}>
                    <Route index element={<PartnerHome />} />
                    <Route path="notificacoes" element={<PartnerNotifications />} />
                    <Route path="obras/:leadId" element={<PartnerObra />} />
                    <Route path="obras/:leadId/nova" element={<PartnerUpdateForm />} />
                    <Route path="obras/:leadId/atualizacoes/:updateId" element={<PartnerUpdateForm />} />
                    <Route path="obras/:leadId/questoes/nova" element={<PartnerNewThread />} />
                    <Route path="obras/:leadId/questoes/:threadId" element={<PartnerThread />} />
                </Route>
                <Route path="*" element={<Navigate to="/parceiros" replace />} />
            </Routes>
        </PartnerAuthProvider>
    );
}
